import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

export const COMPLAINT_ENDPOINTS = Object.freeze({
  types: '/api/v1/complaints/types',
  departments: '/api/v1/complaints/departments',
  departmentTree: '/api/v1/complaints/departments/tree',
  priorities: '/api/v1/complaints/priorities',
  workflows: '/api/v1/complaints/workflows',
  workflowSteps: '/api/v1/complaints/workflows/steps',
  statuses: '/api/v1/complaints/statuses',
});

const rejectRequest = (error, thunkApi) =>
  thunkApi.rejectWithValue({
    status: error?.response?.status ?? error?.status,
    message:
      error?.response?.data?.message ??
      error?.response?.data?.error ??
      error?.message ??
      'Something went wrong',
  });

const request = (type, handler) =>
  createAsyncThunk(type, async (params, thunkApi) => {
    try {
      const response = await handler(params, thunkApi);
      return response.data;
    } catch (error) {
      return rejectRequest(error, thunkApi);
    }
  });

const resourceActions = (resource, endpoint) => ({
  list: request(`complaint/${resource}/list`, (params, { signal }) =>
    api.get(endpoint, { params, signal }),
  ),
  getById: request(`complaint/${resource}/getById`, (id, { signal }) =>
    api.get(`${endpoint}/${id}`, { signal }),
  ),
  create: request(`complaint/${resource}/create`, (payload) =>
    api.post(endpoint, payload),
  ),
  update: request(`complaint/${resource}/update`, ({ id, data }) =>
    api.patch(`${endpoint}/${id}`, data),
  ),
  remove: request(`complaint/${resource}/remove`, (id) =>
    api.delete(`${endpoint}/${id}`),
  ),
});

export const complaintTypeActions = resourceActions(
  'types',
  COMPLAINT_ENDPOINTS.types,
);
export const complaintDepartmentActions = resourceActions(
  'departments',
  COMPLAINT_ENDPOINTS.departments,
);
export const complaintPriorityActions = resourceActions(
  'priorities',
  COMPLAINT_ENDPOINTS.priorities,
);
export const complaintWorkflowActions = resourceActions(
  'workflows',
  COMPLAINT_ENDPOINTS.workflows,
);
export const complaintStatusActions = resourceActions(
  'statuses',
  COMPLAINT_ENDPOINTS.statuses,
);

export const getDepartmentTree = request(
  'complaint/departments/getTree',
  (_params, { signal }) =>
    api.get(COMPLAINT_ENDPOINTS.departmentTree, { signal }),
);

export const getWorkflowSteps = request(
  'complaint/workflows/getSteps',
  (workflowId, { signal }) =>
    api.get(`${COMPLAINT_ENDPOINTS.workflowSteps}/${workflowId}`, { signal }),
);

export const saveWorkflowSteps = request(
  'complaint/workflows/saveSteps',
  (payload) => api.post(COMPLAINT_ENDPOINTS.workflowSteps, payload),
);

export const deleteWorkflowSteps = request(
  'complaint/workflows/deleteSteps',
  (workflowId) =>
    api.delete(`${COMPLAINT_ENDPOINTS.workflowSteps}/${workflowId}`),
);

// Product-module style named exports for consumers that prefer direct thunks.
export const getComplaintTypes = complaintTypeActions.list;
export const getComplaintTypeById = complaintTypeActions.getById;
export const createComplaintType = complaintTypeActions.create;
export const updateComplaintType = complaintTypeActions.update;
export const deleteComplaintType = complaintTypeActions.remove;
export const getComplaintDepartments = complaintDepartmentActions.list;
export const getComplaintDepartmentById = complaintDepartmentActions.getById;
export const createComplaintDepartment = complaintDepartmentActions.create;
export const updateComplaintDepartment = complaintDepartmentActions.update;
export const deleteComplaintDepartment = complaintDepartmentActions.remove;
export const getComplaintPriorities = complaintPriorityActions.list;
export const getComplaintPriorityById = complaintPriorityActions.getById;
export const createComplaintPriority = complaintPriorityActions.create;
export const updateComplaintPriority = complaintPriorityActions.update;
export const deleteComplaintPriority = complaintPriorityActions.remove;
export const getComplaintWorkflows = complaintWorkflowActions.list;
export const getComplaintWorkflowById = complaintWorkflowActions.getById;
export const createComplaintWorkflow = complaintWorkflowActions.create;
export const updateComplaintWorkflow = complaintWorkflowActions.update;
export const deleteComplaintWorkflow = complaintWorkflowActions.remove;
export const getComplaintStatuses = complaintStatusActions.list;
export const getComplaintStatusById = complaintStatusActions.getById;
export const createComplaintStatus = complaintStatusActions.create;
export const updateComplaintStatus = complaintStatusActions.update;
export const deleteComplaintStatus = complaintStatusActions.remove;
